import ContentSaveIcon from '@mui/icons-material/Save';
import { Button as MuiButton, Stack } from '@mui/material';
import { FormEvent, useEffect, useMemo } from 'react';
import {
    RecordContextProvider,
    Toolbar,
    useCreate,
    useNotify,
    useUpdate,
} from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import type { SupplyFormValues, SupplyInput } from '@domain/supply';
import { supplySummaryToFormValues } from '@domain/supply';
import { resourceInputPayload } from '@domain/types';
import type { SupplySummary, Url } from '@wwf/sdk';
import { SupplyInputs } from './SupplyInputs';

type DrawerMode = 'create' | 'edit' | 'show';

const defaultValues: SupplyFormValues = {
    name: '',
    description: null,
    enabled: true,
    price: null,
    noPrice: false,
    type: null,
    target: null,
    link: null,
};

function toSupplyPayload(data: SupplyFormValues): SupplyInput {
    return {
        name: data.name.trim(),
        description: data.description?.trim() || null,
        enabled: data.enabled ?? true,
        price:
            data.noPrice || data.price === undefined || data.price === null
                ? null
                : String(data.price),
        type: data.type?.trim() || null,
        target: data.target?.trim() || null,
        link: (data.link?.trim() as Url | null) || null,
        ...resourceInputPayload(
            { inputKey: 'imageInput', idKey: 'imageId' },
            data.imageInput,
            data.imageId
        ),
    };
}

export function SupplyDrawerForm({
    mode,
    record,
    loading,
    onClose,
    onSuccess,
}: {
    mode: DrawerMode;
    record?: SupplySummary;
    loading: boolean;
    onClose(): void;
    onSuccess(): void;
}) {
    const notify = useNotify();
    const [create] = useCreate();
    const [update] = useUpdate();

    const form = useForm<SupplyFormValues>({
        defaultValues,
    });

    const formRecord = useMemo(
        () =>
            record && (mode === 'edit' || mode === 'show')
                ? supplySummaryToFormValues(record)
                : undefined,
        [record, mode]
    );

    useEffect(() => {
        if (formRecord) {
            form.reset(formRecord);
        } else {
            form.reset(defaultValues);
        }
    }, [formRecord, form]);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (mode === 'show') return;
        form.handleSubmit(async (data: SupplyFormValues) => {
            try {
                const payload = toSupplyPayload(data);

                if (mode === 'create') {
                    await create(
                        'supply',
                        {
                            data: payload,
                            meta: {},
                        },
                        {
                            onSuccess: () => {
                                onSuccess();
                                onClose();
                            },
                        }
                    );
                } else {
                    if (!record?.id) {
                        notify('Record not found', { type: 'error' });
                        return;
                    }
                    await update(
                        'supply',
                        {
                            id: record.id,
                            data: payload,
                            previousData: record,
                            meta: {},
                        },
                        {
                            onSuccess: () => {
                                onSuccess();
                                onClose();
                            },
                        }
                    );
                }
            } catch (e) {
                notify(String(e), { type: 'error' });
            }
        })(event);
    };

    const saving = form.formState.isSubmitting;
    const canSave = !loading && !saving;

    return (
        <FormProvider {...form}>
            <RecordContextProvider value={formRecord}>
                <form onSubmit={handleSubmit}>
                    <Stack gap={2}>
                        <SupplyInputs readOnly={mode === 'show'} />
                    </Stack>
                    {mode !== 'show' && (
                        <Toolbar sx={{ mt: 2, px: 0 }}>
                            <MuiButton
                                variant="contained"
                                type="submit"
                                disabled={!canSave}
                                startIcon={<ContentSaveIcon />}
                            >
                                Save
                            </MuiButton>
                            <MuiButton
                                variant="text"
                                onClick={onClose}
                                sx={{ ml: 1 }}
                                disabled={saving}
                            >
                                Cancel
                            </MuiButton>
                        </Toolbar>
                    )}
                </form>
            </RecordContextProvider>
        </FormProvider>
    );
}
